import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Col, Form, Row } from 'antd';
import PropTypes from 'prop-types';

import AddDrawer from '../../common/addDrawer';
import OnTraccrNumberInput from '../../common/inputs/OnTraccrNumberInput';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import CostCodeUploadList from '../../costcodes/costcodeUploadList';
import { currencyFormatter, currencyParser } from '../../helpers/inputParsers';

/** Native phase estimate field names (hours and costs) for useRedesign */
export const PHASE_ESTIMATE_NATIVE_KEYS = [
  'estimatedLabourHours',
  'estimatedLabourCost',
  'estimatedOverheadHours',
  'estimatedOverheadCost',
  'estimatedMaterialCost',
  'estimatedEquipmentHours',
  'estimatedEquipmentCost',
];

function ProjectAddPhaseCodeDrawer({
  visible,
  onDrawerClosed,
  costcodes,
  projectCostcodes,
  onSelectedCostCodesChanged,
  selectedPhase = {},
  useRedesign,
}) {
  const {
    name: phaseName,
    description: phaseDescription,
    costcodes: selectedCostCodes = [],
    id: phaseId,
  } = selectedPhase;

  const categoryIdMap = useSelector((state) => state.costcodes.categoriesIdMap);
  const categories = useSelector((state) => state.costcodes.categories);

  const customCategories = useMemo(() => (
    Object.values(categories || {}).filter((c) => !c?.isDefault)
  ), [categories]);

  // When useRedesign is true, show all project costcodes (both phased and unphased)
  const availableCostcodes = useMemo(() => {
    if (!useRedesign) return costcodes;
    return projectCostcodes || costcodes;
  }, [costcodes, projectCostcodes, useRedesign]);

  const initialValues = useMemo(() => {
    const base = {
      phaseName,
      description: phaseDescription,
    };
    if (!useRedesign) return base;
    const estimates = {};
    PHASE_ESTIMATE_NATIVE_KEYS.forEach((key) => {
      if (selectedPhase[key] != null) estimates[key] = selectedPhase[key];
    });
    customCategories.forEach((cat) => {
      const key = `phaseEstimate.${cat.id}`;
      if (selectedPhase[key] != null) estimates[key] = selectedPhase[key];
    });
    return { ...base, ...estimates };
  }, [phaseName, phaseDescription, useRedesign, selectedPhase, customCategories]);

  const firstCustomCategoryCol = useMemo(() => {
    if (!useRedesign || !customCategories.length) return null;
    const cat = customCategories[0];
    const key = `phaseEstimate.${cat.id}`;
    return (
      <Col key={key} span={12}>
        <Form.Item name={key} label={`Estimated ${cat.name} Cost`}>
          <OnTraccrNumberInput
            className="costcode-form-input"
            min={0}
            style={{ width: '100%' }}
            formatter={currencyFormatter}
            parser={currencyParser}
          />
        </Form.Item>
      </Col>
    );
  }, [useRedesign, customCategories]);

  const customCategoryEstimateRows = useMemo(() => {
    if (!useRedesign || customCategories.length <= 1) return [];
    const rest = customCategories.slice(1);
    return rest
      .reduce((chunks, cat, i) => {
        if (i % 2 === 0) chunks.push([cat]);
        else chunks[chunks.length - 1].push(cat);
        return chunks;
      }, [])
      .map((pair) => (
        <Row
          key={pair.length === 1 ? pair[0].id : `${pair[0].id}-${pair[1].id}`}
          gutter={16}
        >
          {pair.map((cat) => {
            const key = `phaseEstimate.${cat.id}`;
            return (
              <Col key={key} span={12}>
                <Form.Item name={key} label={`Estimated ${cat.name} Cost`}>
                  <OnTraccrNumberInput
                    className="costcode-form-input"
                    min={0}
                    style={{ width: '100%' }}
                    formatter={currencyFormatter}
                    parser={currencyParser}
                  />
                </Form.Item>
              </Col>
            );
          })}
        </Row>
      ));
  }, [useRedesign, customCategories]);

  const formView = ({ formRef } = {}) => (
    <Form
      ref={formRef}
      initialValues={initialValues}
      className="costcode-form-label"
      layout="vertical"
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="phaseName"
            label="Phase"
          >
            <OnTraccrTextInput
              className="costcode-form-input"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="description"
            label="Description"
          >
            <OnTraccrTextInput
              className="costcode-form-input"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </Row>
      {useRedesign && (
        <>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="estimatedLabourHours" label="Estimated Labour Hours">
                <OnTraccrNumberInput
                  className="costcode-form-input"
                  min={0}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="estimatedLabourCost" label="Estimated Labour Cost">
                <OnTraccrNumberInput
                  className="costcode-form-input"
                  min={0}
                  style={{ width: '100%' }}
                  formatter={currencyFormatter}
                  parser={currencyParser}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="estimatedOverheadHours" label="Estimated Overhead Hours">
                <OnTraccrNumberInput
                  className="costcode-form-input"
                  min={0}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="estimatedOverheadCost" label="Estimated Overhead Cost">
                <OnTraccrNumberInput
                  className="costcode-form-input"
                  min={0}
                  style={{ width: '100%' }}
                  formatter={currencyFormatter}
                  parser={currencyParser}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="estimatedMaterialCost" label="Estimated Material Cost">
                <OnTraccrNumberInput
                  className="costcode-form-input"
                  min={0}
                  style={{ width: '100%' }}
                  formatter={currencyFormatter}
                  parser={currencyParser}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="estimatedEquipmentHours" label="Estimated Equipment Hours">
                <OnTraccrNumberInput
                  className="costcode-form-input"
                  min={0}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="estimatedEquipmentCost" label="Estimated Equipment Cost">
                <OnTraccrNumberInput
                  className="costcode-form-input"
                  min={0}
                  style={{ width: '100%' }}
                  formatter={currencyFormatter}
                  parser={currencyParser}
                />
              </Form.Item>
            </Col>
            {firstCustomCategoryCol}
          </Row>
          {customCategoryEstimateRows}
        </>
      )}
      <CostCodeUploadList
        selectable
        categories={categoryIdMap}
        dataSource={availableCostcodes}
        defaultSelected={selectedCostCodes.map((cc) => cc.id)}
        onSelectedChanged={onSelectedCostCodesChanged}
      />
    </Form>
  );

  return (
    <AddDrawer
      onClose={onDrawerClosed}
      visible={visible}
      title={`${phaseId ? 'Edit' : 'Add'} Phase`}
      isAdd
      formView={formView}
      width={750}
      key={phaseName} // Fresh component every time
    />
  );
}

/* eslint-disable react/forbid-prop-types */
ProjectAddPhaseCodeDrawer.propTypes = {
  visible: PropTypes.bool,
  onDrawerClosed: PropTypes.func.isRequired,
  costcodes: PropTypes.array.isRequired,
  projectCostcodes: PropTypes.array,
  onSelectedCostCodesChanged: PropTypes.func.isRequired,
  selectedPhase: PropTypes.object,
  useRedesign: PropTypes.bool,
};

ProjectAddPhaseCodeDrawer.defaultProps = {
  selectedPhase: {},
  visible: false,
  projectCostcodes: [],
  useRedesign: false,
};

export default ProjectAddPhaseCodeDrawer;
